import React from 'react';
import verfied from '../../assets/images/verified.svg';
import notVerfied from '../../assets/images/verified_Grey.svg';
import vipVerified from '../../assets/images/VIP_Member_icon.svg';

/**
 * Profile metadata bits that used to be built as HTML strings and injected with
 * `dangerouslySetInnerHTML` in three separate files.
 *
 * Two reasons they are components now:
 *
 * 1. `getResidenceCity` / `getOriginState` interpolated the viewer's city,
 *    country and origin state — user-controlled profile fields — straight into
 *    an HTML string. A profile whose city was set to markup executed script in
 *    every other user's browser. React escapes these values automatically.
 *
 * 2. The badge markup wrote `className` into a raw HTML string, where the real
 *    attribute is `class`, so the styling silently never applied.
 *
 * Keep the plan-id mapping here rather than in the callers: free mode reports
 * every user as plan 11, which is what makes the VIP badge show.
 */

const TIERS = {
  vip: { src: vipVerified, label: 'VIP Member' },
  premium: { src: verfied, label: 'Premium Member' },
  basic: { src: notVerfied, label: 'Basic Member' }
};

/**
 * `user` is null for anonymous visitors and until the profile request resolves,
 * so every read is guarded.
 */
export const resolvePlanTier = (user, premiumPlanIds = [9, 10]) => {
  const { currentPlanId: planId, isPlanExpired } = user || {};

  if (!isPlanExpired) {
    if (planId === 11) return 'vip';
    if (premiumPlanIds.includes(planId)) return 'premium';
  }
  return 'basic';
};

export const PlanBadge = ({
  user,
  iconOnly = false,
  className = '',
  style,
  premiumPlanIds
}) => {
  const { src, label } = TIERS[resolvePlanTier(user, premiumPlanIds)];

  return (
    <>
      <img className={className || undefined} src={src} alt="" style={style} />
      {!iconOnly && <span>{` ${label}`}</span>}
    </>
  );
};

export const extractFlagEmoji = (str) => {
  if (!str) return '';

  const matches = str.match(/[\u{1F1E6}-\u{1F1FF}]{2}/gu);
  return matches ? matches[0] : '';
};

/**
 * City + country flag, or country + flag when the city is hidden.
 * `isProfilePrivate === 'YES'` suppresses the city; callers that do not pass it
 * (the match queue) always show the city, which is the behaviour they had.
 */
export const ResidenceCity = ({ address, isProfilePrivate }) => {
  const country = address?.country ?? '';
  const city = address?.city ?? '';
  const flag = extractFlagEmoji(country);

  if (city && isProfilePrivate !== 'YES') {
    return (
      <>
        <span>{city}</span> <span>{flag}</span>
      </>
    );
  }

  return (
    <>
      <span>{country.replace(flag, '')}</span> <span>{flag}</span>
    </>
  );
};

/**
 * Origin state + the origin country's flag. `origin1`/`origin2` are JSON blobs
 * written by the profile form; a malformed one used to throw and blank the
 * whole row, so parsing is defensive.
 */
const parseLabel = (raw) => {
  if (!raw) return '';
  try {
    return JSON.parse(raw)?.label ?? '';
  } catch (err) {
    return '';
  }
};

export const OriginState = ({ uDetails }) => {
  if (!uDetails) return null;

  const country = parseLabel(uDetails.origin1);
  const state = parseLabel(uDetails.origin2);

  return (
    <>
      <span>{state}</span> <span>{extractFlagEmoji(country)}</span>
    </>
  );
};

export default PlanBadge;
